import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Banner,
  BlockStack,
  Card,
  InlineStack,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris'
import { engineApi, type DeliveryHistoryItem } from '../../../sdk/engine.api'
import { getShop } from '../../../sdk/shop'
import { SectionSidebar } from '../../../components/layout/SectionSidebar'

const SIDEBAR_ITEMS = [
  { label: 'Queue',    href: '/workspace/delivery-tracking/queue'    },
  { label: 'Activity', href: '/workspace/delivery-tracking/activity' },
]

// Activity shows all orders with scan_pickup_at, including delivered ones — full history.

const OUTCOME_FILTER_OPTIONS = [
  { label: 'All',                 value: '' },
  { label: 'Delivered',           value: 'delivered' },
  { label: 'Failed — Refused',    value: 'customer_refused' },
  { label: 'Failed — Courier',    value: 'courier_mistake' },
  { label: 'Returned',            value: 'returned_to_business' },
]

function fmt(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' })
}

function durationHrs(from: string, to: string): string {
  if (!from || !to) return '—'
  const hrs = (new Date(to).getTime() - new Date(from).getTime()) / 3_600_000
  if (hrs >= 24) return `${(hrs / 24).toFixed(1)}d`
  return `${hrs.toFixed(1)}h`
}

export function DeliveryTrackingActivityPage() {
  const shop = getShop() ?? ''
  const [items,   setItems]   = useState<DeliveryHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [search,  setSearch]  = useState('')
  const [filter,  setFilter]  = useState('')

  const load = useCallback(() => {
    if (!shop) return
    setLoading(true)
    // Load ALL items including delivered (no status filter)
    engineApi.deliveryHistory(shop)
      .then(r => setItems(r.items))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }, [shop])

  useEffect(() => { load() }, [load])

  const filtered = items.filter(e => {
    if (filter === 'customer_refused' || filter === 'courier_mistake') {
      if (e.failedOutcome !== filter) return false
    } else if (filter) {
      if (e.deliveryStatus !== filter) return false
    }
    if (search) {
      const q = search.toLowerCase()
      return (
        e.orderName.toLowerCase().includes(q) ||
        e.customerName.toLowerCase().includes(q) ||
        e.trackingNumber.toLowerCase().includes(q)
      )
    }
    return true
  })

  // Sort: most recently dispatched first
  const sorted = [...filtered].sort(
    (a, b) => new Date(b.scanPickupAt).getTime() - new Date(a.scanPickupAt).getTime(),
  )

  return (
    <Page
      title="Delivery Activity"
      subtitle="Complete history of dispatched shipments"
      backAction={{ content: 'Workspace', url: '/workspace-hub' }}
      primaryAction={{ content: 'Refresh', onAction: load, loading }}
    >
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
      <SectionSidebar items={SIDEBAR_ITEMS} />
      <div style={{ flex: 1, minWidth: 0 }}>
      <BlockStack gap="400">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}><Text as="p">{error}</Text></Banner>}

        <Card>
          <InlineStack gap="300" wrap={false}>
            <div style={{ flex: 2 }}>
              <TextField
                label="Search"
                labelHidden
                value={search}
                onChange={setSearch}
                placeholder="Order, customer, tracking number…"
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setSearch('')}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <Select
                label="Outcome"
                labelHidden
                options={OUTCOME_FILTER_OPTIONS}
                value={filter}
                onChange={setFilter}
              />
            </div>
          </InlineStack>
        </Card>

        {loading && items.length === 0 ? (
          <InlineStack align="center"><Spinner accessibilityLabel="Loading" /></InlineStack>
        ) : sorted.length === 0 ? (
          <Card>
            <Text as="p" variant="bodySm" tone="subdued">No records match the current filters.</Text>
          </Card>
        ) : (
          <BlockStack gap="200">
            <Text as="p" variant="bodySm" tone="subdued">{sorted.length} records</Text>
            {sorted.map(e => (
              <div
                key={`${e.orderName}-${e.scanPickupAt}`}
                style={{
                  border: '1px solid var(--p-color-border)',
                  borderRadius: 'var(--p-border-radius-200)',
                  padding: '12px 16px',
                }}
              >
                <InlineStack gap="300" align="space-between" blockAlign="start" wrap={false}>
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">{e.orderName}</Text>
                      {e.carrierName && <Badge>{e.carrierName}</Badge>}
                      {e.integrationType === 'manual' && <Badge tone="attention">Manual</Badge>}
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {e.customerName || '—'} · TN: {e.trackingNumber || '—'}
                    </Text>
                    {e.failedReason && (
                      <Text as="p" variant="bodySm" tone="critical">{e.failedReason}</Text>
                    )}
                  </BlockStack>

                  <BlockStack gap="100" inlineAlign="end">
                    <Text as="p" variant="bodySm" tone="subdued">Dispatched: {fmt(e.scanPickupAt)}</Text>
                    {e.deliveredAt && (
                      <Text as="p" variant="bodySm" tone="success">
                        Delivered: {fmt(e.deliveredAt)} ({durationHrs(e.scanPickupAt, e.deliveredAt)})
                      </Text>
                    )}
                    {e.failedAt && (
                      <Text as="p" variant="bodySm" tone="critical">Failed: {fmt(e.failedAt)}</Text>
                    )}
                    {e.returnedAt && (
                      <Text as="p" variant="bodySm" tone="caution">
                        Returned: {fmt(e.returnedAt)}
                        {e.scanReturnAt ? ` · Received: ${fmt(e.scanReturnAt)}` : ' · ⚠ Not yet received'}
                      </Text>
                    )}
                  </BlockStack>
                </InlineStack>
              </div>
            ))}
          </BlockStack>
        )}
      </BlockStack>
      </div>
      </div>
    </Page>
  )
}
